import { mapEase } from "./ease";
import { scheduleNext } from "./timing";

// Twinkle animation:
// Iterates through child drawable SVG elements (paths, shapes, groups) and for each
// performs a quick shrink (scale) + fade (opacity) then returns to normal.
// Children overlap in time via offset so sequence feels continuous.
// loopBehavior: once | infinite | sporadic
export const twinkleAnim = (
	element,
	{
		gsap,
		loopBehavior = "infinite",
		animatedElements,
		sporadicTimers,
		duration = 1, // total time per child (shrink + return since we use yoyo repeat:1)
		ease = "none",
		scaleFactor = 0.85,
		opacityFactor = 0.4,
		overlapRatio = 0.3, // 0..1 portion of previous child's duration after which next starts
		randomDelayMin = 3000,
		randomDelayMax = 8000,
	} = {},
) => {
	if (!element || !gsap || !animatedElements) return null;
	if (animatedElements.has(element)) return animatedElements.get(element);

	let observer;
	let tl = null;
	let isVisible = false;

	// Collect drawable targets inside the SVG; fallback to element itself
	let targets = [];
	try {
		targets = Array.from(element.querySelectorAll("path, circle, rect, polygon, line, polyline, ellipse, use"));
	} catch (e) {
		// ignore query issues
	}
	if (!targets.length) targets = [element];

	const mappedEase = mapEase(ease);
	const perChildDuration = Math.max(0.05, duration); // safety floor
	const offsetStep = perChildDuration * overlapRatio;

	const buildTimeline = () => {
		const timeline = gsap.timeline({
			onComplete: () => {
				if (loopBehavior === "sporadic" && animatedElements.has(element) && isVisible) {
					// mark as idle then schedule next run
					animatedElements.set(element, null);
					scheduleNext({
						element,
						animatedElements,
						sporadicTimers,
						startSingleCycle: startSingleCycle, // schedule rebuild & play
						randomDelayMin,
						randomDelayMax,
					});
				}
			},
		});

		targets.forEach((t, i) => {
			timeline.fromTo(
				t,
				{ scale: 1, opacity: 1, transformOrigin: "center center" },
				{
					scale: scaleFactor,
					opacity: opacityFactor,
					duration: perChildDuration / 2,
					ease: mappedEase,
					yoyo: true,
					repeat: 1,
				},
				i * offsetStep,
			);
		});
		return timeline;
	};

	const startSingleCycle = () => {
		if (isVisible) {
			tl = buildTimeline();
			animatedElements.set(element, tl);
		}
	};

	const startAnimation = () => {
		if (loopBehavior === "sporadic") {
			if (!animatedElements.has(element) || !animatedElements.get(element)) {
				startSingleCycle();
			} else {
				const currentTl = animatedElements.get(element);
				if (currentTl) currentTl.play();
			}
		} else {
			if (animatedElements.has(element) && animatedElements.get(element)) {
				animatedElements.get(element).play();
				return;
			}
			switch (loopBehavior) {
				case "once":
					tl = buildTimeline();
					break;
				case "infinite":
					tl = buildTimeline();
					tl.repeat(-1);
					break;
				default:
					tl = buildTimeline();
			}
			animatedElements.set(element, tl);
		}
	};

	const stopAnimation = () => {
		const currentTl = animatedElements.get(element);
		if (currentTl) {
			currentTl.pause();
		}
		// Cancel any scheduled sporadic timers
		if (loopBehavior === "sporadic" && sporadicTimers && sporadicTimers.has(element)) {
			clearTimeout(sporadicTimers.get(element));
			sporadicTimers.delete(element);
		}
	};

	observer = new IntersectionObserver(
		(entries) => {
			entries.forEach((entry) => {
				isVisible = entry.isIntersecting;
				if (isVisible) {
					startAnimation();
				} else {
					stopAnimation();
				}
			});
		},
		{ threshold: 0.1 },
	);

	observer.observe(element);
	element._twinkleObserver = observer;

	return tl;
};

// Preview: single pass overlapping sequence
export const twinklePreview = (
	element,
	{ gsap, duration = 1, ease = "ease", scaleFactor = 0.85, opacityFactor = 0.4, overlapRatio = 0.3 } = {},
) => {
	if (!element || !gsap) return null;
	let targets = [];
	try {
		targets = Array.from(element.querySelectorAll("path, circle, rect, polygon, line, polyline, ellipse, use"));
	} catch (e) {}
	if (!targets.length) targets = [element];

	const mappedEase = mapEase(ease);
	const perChildDuration = Math.max(0.05, duration);
	const offsetStep = perChildDuration * overlapRatio;
	const tl = gsap.timeline();
	targets.forEach((t, i) => {
		tl.fromTo(
			t,
			{ scale: 1, opacity: 1, transformOrigin: "center center" },
			{
				scale: scaleFactor,
				opacity: opacityFactor,
				duration: perChildDuration / 2,
				ease: mappedEase,
				yoyo: true,
				repeat: 1,
			},
			i * offsetStep,
		);
	});
	return tl;
};

// (stop handled by shared stopManagedAnimation in timing.js)
